<template>
  <div ref="map" class="ais-map-container" />
</template>

<script>
import L from 'leaflet'
import { createShipIcon } from '@/utils/shipIcon'
import { elBackAxios } from '@/services/myAxios'

const DEFAULT_CENTER = [0, 0]
const DEFAULT_ZOOM = 4
const MIN_ZOOM = 3
const MAX_ZOOM = 17

export default {
  name: 'AISMapCanvas',
  props: {
    targets: {
      type: Array,
      default: () => [],
    },
    selectedId: {
      type: String,
      default: null,
    },
    ownship: {
      type: Object,
      default: null,
    },
    route: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      map: null,
      targetLayer: null,
      routeLayer: null,
      markers: new Map(),
      markerTargets: new Map(),
      shipMarker: null,
      activePopup: null,
      popupTimer: null,
      expandedPopupData: null,
      expandedPopupTargetId: null,
    }
  },
  watch: {
    targets: {
      handler() {
        this.updateMarkers()
      },
      deep: false,
    },
    selectedId() {
      this.updateSelection()
    },
    ownship: {
      handler() {
        this.updateOwnShip()
      },
      deep: false,
    },
    route: {
      handler() {
        this.updateRoute()
      },
      deep: false,
    },
  },
  mounted() {
    this.initMap()
  },
  beforeDestroy() {
    this.disposeMap()
  },
  methods: {
    initMap() {
      if (this.map) {
        return
      }
      this.map = L.map(this.$refs.map, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        minZoom: MIN_ZOOM,
        maxZoom: MAX_ZOOM,
        worldCopyJump: true,
        preferCanvas: true,
      })

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(this.map)

      this.targetLayer = L.layerGroup().addTo(this.map)
      this.routeLayer = L.layerGroup().addTo(this.map)

      this.map.on('moveend', this.handleViewportChange)
      this.map.on('zoomend', this.handleViewportChange)

      this.handleViewportChange()
      this.updateRoute()
      this.updateOwnShip()
      this.updateMarkers()
    },
    disposeMap() {
      if (!this.map) {
        return
      }
      this.map.off('moveend', this.handleViewportChange)
      this.map.off('zoomend', this.handleViewportChange)
      this.map.remove()
      this.map = null
      this.targetLayer = null
      this.routeLayer = null
      this.markers.clear()
      this.markerTargets.clear()
      if (this.popupTimer) {
        clearTimeout(this.popupTimer)
        this.popupTimer = null
      }
      this.activePopup = null
      this.shipMarker = null
    },
    handleViewportChange() {
      if (!this.map) {
        return
      }
      const bounds = this.map.getBounds()
      this.$emit('bounds-change', {
        bounds: {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        },
        zoom: this.map.getZoom(),
      })
    },
    updateMarkers() {
      if (!this.map || !this.targetLayer) {
        return
      }
      const list = this.targets || []
      const nextIds = new Set(list.map(target => target.id))

      this.markers.forEach((marker, id) => {
        if (!nextIds.has(id)) {
          this.targetLayer.removeLayer(marker)
          this.markers.delete(id)
          this.markerTargets.delete(id)
        }
      })

      list.forEach(target => {
        if (!this.isTargetValid(target)) {
          return
        }
        const latlng = [target.lat, target.lon]
        const isSelected = target.id === this.selectedId
        const isMoving = this.isTargetMoving(target)
        const desiredKind = isMoving ? 'ship' : 'circle'

        let marker = this.markers.get(target.id)
        if (marker && marker.__aisKind !== desiredKind) {
          this.targetLayer.removeLayer(marker)
          this.markers.delete(target.id)
          marker = null
        }

        if (!marker) {
          marker = desiredKind === 'ship'
            ? this.createShipMarker(target, isSelected)
            : this.createCircleMarker(target, isSelected)
          marker.__aisKind = desiredKind
          marker.on('click', () => this.onMarkerClick(target))
          marker.addTo(this.targetLayer)
          this.markers.set(target.id, marker)
        }

        marker.setLatLng(latlng)
        if (marker.__aisKind === 'ship') {
          marker.setIcon(this.makeTargetShipIcon(target, isSelected))
        } else {
          marker.setStyle(this.resolveMarkerStyle(target, isSelected))
        }

        this.markerTargets.set(target.id, target)
      })

      this.updateSelection()
    },
    updateSelection() {
      if (!this.markers.size) {
        return
      }
      this.markers.forEach((marker, id) => {
        const target = this.markerTargets.get(id)
        if (!target) {
          return
        }
        const isSelected = id === this.selectedId
        if (marker.__aisKind === 'ship') {
          marker.setIcon(this.makeTargetShipIcon(target, isSelected))
        } else {
          marker.setStyle(this.resolveMarkerStyle(target, isSelected))
        }
        if (marker.setZIndexOffset) {
          marker.setZIndexOffset(isSelected ? 1000 : 0)
        }
        if (isSelected && marker.bringToFront) {
          marker.bringToFront()
        }
      })
    },
    createCircleMarker(target, isSelected) {
      return L.circleMarker([target.lat, target.lon], this.resolveMarkerStyle(target, isSelected))
    },
    createShipMarker(target, isSelected) {
      return L.marker([target.lat, target.lon], {
        icon: this.makeTargetShipIcon(target, isSelected),
        keyboard: false,
      })
    },
    makeTargetShipIcon(target, isSelected) {
      const heading = Number(target?.course)
      return createShipIcon({
        width: 26,
        height: 54,
        scale: 0.6,
        headingDeg: Number.isFinite(heading) ? heading : 0,
        fillColor: isSelected ? '#ea580c' : '#059669',
        strokeColor: isSelected ? '#fed7aa' : '#a7f3d0',
        strokeWidth: 1.2,
        className: 'ais-target-icon',
        alt: 'AIS target',
      })
    },
    resolveMarkerStyle(target, isSelected) {
      if (isSelected) {
        return {
          radius: 8,
          color: '#ea580c',
          weight: 2,
          fillColor: '#fb923c',
          fillOpacity: 0.88,
        }
      }
      return {
        radius: 6,
        color: '#4b5563',
        weight: 1.5,
        fillColor: '#a1a1aa',
        fillOpacity: 0.82,
      }
    },
    isTargetMoving(target) {
      const speed = Number(target?.speed)
      return Number.isFinite(speed) && speed > 0.1
    },
    isTargetValid(target) {
      return target && Number.isFinite(target.lat) && Number.isFinite(target.lon)
    },
    onMarkerClick(target) {
      this.$emit('target-click', target)
      this.showPopup(target)
    },
    showPopup(target) {
      if (!this.map || !this.isTargetValid(target)) {
        return
      }
      if (this.activePopup) {
        this.map.closePopup(this.activePopup)
        this.activePopup = null
      }
      
      const isExpanded = this.expandedPopupTargetId === target.id && this.expandedPopupData
      let content
      
      if (isExpanded) {
        content = this.buildStyledPopupContent(target, this.expandedPopupData)
      } else {
        content = this.buildBasicPopupContent(target)
      }
      
      this.activePopup = L.popup({
        closeButton: false,
        autoClose: true,
        autoPan: true,
        className: 'ais-map-popup',
        maxWidth: 300,
      })
        .setLatLng([target.lat, target.lon])
        .setContent(content)
        .openOn(this.map)

      // Добавляем обработчик для кнопки Details
      if (!isExpanded) {
        this.$nextTick(() => {
          const btn = document.querySelector(`#ais-details-btn-${target.id}`)
          if (btn) {
            btn.addEventListener('click', () => this.fetchAISDetails(target))
          }
        })
      }

      if (this.popupTimer) {
        clearTimeout(this.popupTimer)
      }
    },

    /**
     * Получить текстовый статус навигации по коду
     */
    getNavStatusLabel(navStatusCode) {
      const navStatusMap = {
        0: 'Under way using engine',
        1: 'At anchor',
        2: 'Not under command',
        3: 'Restricted manoeuverability',
        4: 'Constrained by draught',
        5: 'Moored',
        6: 'Aground',
        7: 'Engaged in Fishing',
        8: 'Under way sailing',
        9: 'HSC',
        10: 'WIG',
        11: 'Reserved',
        12: 'Reserved',
        13: 'Reserved',
        14: 'AIS-SART active',
        15: 'Not defined'
      }
      const code = Number(navStatusCode)
      return Number.isFinite(code) && code in navStatusMap ? navStatusMap[code] : ''
    },

    /**
     * Получить вариант статуса для стилизации
     */
    getNavStatusVariant(navStatusCode) {
      const code = Number(navStatusCode)
      if (code === 1 || code === 5) return 'success'      // At anchor, Moored
      if (code === 2 || code === 3 || code === 6) return 'warning' // Not under command, Restricted, Aground
      if (code === 14) return 'info'                       // AIS-SART
      if (code === 2 || code === 6) return 'danger'       // Aground, etc
      return 'secondary'
    },

    /**
     * Получить расшифрованный тип судна по коду
     */
    getShipTypeLabel(shipTypeCode) {
      const shipTypeMap = {
        0: 'Unknown',
        3: 'Tug, Pilot',
        4: 'High Speed',
        6: 'Passenger',
        7: 'Cargo',
        8: 'Tanker',
        9: 'Yachts',
        10: 'Fishing',
        11: 'Base Stations',
        12: 'Air Craft',
        13: 'Navigation Aid',
      }
      if (shipTypeCode === null || shipTypeCode === undefined || shipTypeCode === '') {
        return 'Vessel'
      }
      const numeric = Number(shipTypeCode)
      if (Number.isFinite(numeric)) {
        const direct = shipTypeMap[numeric]
        if (direct) {
          return direct
        }
        const tens = Math.floor(numeric / 10) * 10
        return shipTypeMap[tens] || 'Vessel'
      }
      return 'Vessel'
    },

    /**
     * Построить базовый попап (без развёрнутых данных)
     */
    buildBasicPopupContent(target) {
      const speed = this.formatNumber(target.speed, 'kn')
      const course = this.formatNumber(target.course, '°')
      const detailsButtonId = `ais-details-btn-${target.id}`
      
      const infoRows = `
        <div class="ship-popup-info-row">
          <div class="ship-popup-info-label">MMSI</div>
          <div class="ship-popup-info-value">${this.escapeHtml(target.mmsi || '—')}</div>
        </div>
        <div class="ship-popup-info-row">
          <div class="ship-popup-info-label">Speed</div>
          <div class="ship-popup-info-value">${speed}</div>
        </div>
        <div class="ship-popup-info-row">
          <div class="ship-popup-info-label">Course</div>
          <div class="ship-popup-info-value">${course}</div>
        </div>
      `
      
      const navStatus = target.navStatus
      const statusLabel = this.getNavStatusLabel(navStatus)
      const statusVariant = this.getNavStatusVariant(navStatus)
      const statusBadge = statusLabel ? `<span class="ship-popup-status ship-popup-status-${statusVariant}">${this.escapeHtml(statusLabel)}</span>` : ''
      
      return `
        <div class="ship-popup-content scrollable-content">
          <div class="ship-popup-card">
            <div class="ship-popup-header">
              <div class="ship-popup-header-info">
                <div class="ship-popup-label">${this.escapeHtml(this.getShipTypeLabel(target.type))}</div>
                <div class="ship-popup-title">${this.escapeHtml(target.name || 'Unknown vessel')}</div>
              </div>
              ${statusBadge}
            </div>
            
            <div class="ship-popup-info-grid">
              ${infoRows}
            </div>
            
            <div class="ship-popup-details-container">
              <button id="${detailsButtonId}" type="button" class="ship-popup-toggle">Show details</button>
            </div>
          </div>
        </div>
      `
    },

    /**
     * Построить развёрнутый попап со всеми данными AIS
     */
    buildStyledPopupContent(target, data) {
      if (!data) {
        return this.buildBasicPopupContent(target)
      }
      
      // Основная информация
      const primaryRows = this.buildPrimaryInfoRows(target, data)
      
      // Дополнительные секции
      const additionalSections = this.buildAdditionalSections(data)
      
      const navStatus = data.nav_status || target.navStatus
      const statusLabel = this.getNavStatusLabel(navStatus)
      const statusVariant = this.getNavStatusVariant(navStatus)
      const statusBadge = statusLabel ? `<span class="ship-popup-status ship-popup-status-${statusVariant}">${this.escapeHtml(statusLabel)}</span>` : ''
      
      return `
        <div class="ship-popup-content scrollable-content">
          <div class="ship-popup-card">
            <div class="ship-popup-header">
              <div class="ship-popup-header-info">
                <div class="ship-popup-label">${this.escapeHtml(this.getShipTypeLabel(target.type))}</div>
                <div class="ship-popup-title">${this.escapeHtml(target.name || 'Unknown vessel')}</div>
              </div>
              ${statusBadge}
            </div>
            
            <div class="ship-popup-info-grid">
              ${primaryRows}
            </div>
            
            ${additionalSections}
          </div>
        </div>
      `
    },



    /**
     * Построить основные информационные ряды
     */
    buildPrimaryInfoRows(target, data) {
      const rows = []
      
      if (target.mmsi) {
        rows.push(`
          <div class="ship-popup-info-row">
            <div class="ship-popup-info-label">MMSI</div>
            <div class="ship-popup-info-value">${this.escapeHtml(target.mmsi)}</div>
          </div>
        `)
      }
      
      if (target.speed !== null && target.speed !== undefined) {
        rows.push(`
          <div class="ship-popup-info-row">
            <div class="ship-popup-info-label">Speed</div>
            <div class="ship-popup-info-value">${this.formatNumber(target.speed, 'kn')}</div>
          </div>
        `)
      }
      
      if (target.course !== null && target.course !== undefined) {
        rows.push(`
          <div class="ship-popup-info-row">
            <div class="ship-popup-info-label">Course</div>
            <div class="ship-popup-info-value">${this.formatNumber(target.course, '°')}</div>
          </div>
        `)
      }
      
      if (data.vessel_type) {
        rows.push(`
          <div class="ship-popup-info-row">
            <div class="ship-popup-info-label">Type</div>
            <div class="ship-popup-info-value">${this.escapeHtml(data.vessel_type)}</div>
          </div>
        `)
      }
      
      if (data.flag) {
        rows.push(`
          <div class="ship-popup-info-row">
            <div class="ship-popup-info-label">Flag</div>
            <div class="ship-popup-info-value">${this.escapeHtml(data.flag)}</div>
          </div>
        `)
      }
      
      if (data.callsign) {
        rows.push(`
          <div class="ship-popup-info-row">
            <div class="ship-popup-info-label">Callsign</div>
            <div class="ship-popup-info-value">${this.escapeHtml(data.callsign)}</div>
          </div>
        `)
      }
      
      return rows.join('')
    },

    /**
     * Построить дополнительные секции данных
     */
    buildAdditionalSections(data) {
      const sections = []
      
      // Construction (размеры)
      const constructionRows = []
      if (data.gt) {
        constructionRows.push(this.renderFieldRow('Gross Tonnage', this.escapeHtml(String(data.gt))))
      }
      if (data.dwt) {
        constructionRows.push(this.renderFieldRow('Deadweight', this.escapeHtml(String(data.dwt))))
      }
      if (data.built) {
        constructionRows.push(this.renderFieldRow('Built', this.escapeHtml(String(data.built))))
      }
      if (data.draught) {
        constructionRows.push(this.renderFieldRow('Draught', `${this.escapeHtml(String(data.draught))} m`))
      }
      if (data.size_a || data.size_b || data.size_c || data.size_d) {
        const sizeA = Number(data.size_a) || 0
        const sizeB = Number(data.size_b) || 0
        const sizeC = Number(data.size_c) || 0
        const sizeD = Number(data.size_d) || 0
        
        const length = sizeA + sizeB
        const width = sizeC + sizeD
        
        if (length > 0) {
          constructionRows.push(this.renderFieldRow('Length', `${length} m`))
        }
        if (width > 0) {
          constructionRows.push(this.renderFieldRow('Width', `${width} m`))
        }
      }
      
      if (constructionRows.length) {
        sections.push(this.renderSection('Construction', constructionRows.join('')))
      }
      
      // Voyage (маршрут и порты)
      const voyageRows = []
      if (data.destination) {
        voyageRows.push(this.renderFieldRow('Destination', this.escapeHtml(data.destination)))
      }
      if (data.eta) {
        voyageRows.push(this.renderFieldRow('ETA', this.formatDatetime(data.eta)))
      }
      if (data.current_port) {
        voyageRows.push(this.renderFieldRow('Current Port', this.escapeHtml(data.current_port)))
      }
      if (data.last_port) {
        voyageRows.push(this.renderFieldRow('Last Port', this.escapeHtml(data.last_port)))
      }
      if (data.last_port_country) {
        voyageRows.push(this.renderFieldRow('Last Port Country', this.escapeHtml(data.last_port_country)))
      }
      if (data.last_port_dep_utc) {
        voyageRows.push(this.renderFieldRow('Last Port Dep', this.formatDatetime(data.last_port_dep_utc)))
      }
      if (data.next_port) {
        voyageRows.push(this.renderFieldRow('Next Port', this.escapeHtml(data.next_port)))
      }
      if (data.next_port_country) {
        voyageRows.push(this.renderFieldRow('Next Port Country', this.escapeHtml(data.next_port_country)))
      }
      if (data.next_port_eta_utc) {
        voyageRows.push(this.renderFieldRow('Next Port ETA', this.formatDatetime(data.next_port_eta_utc)))
      }
      
      if (voyageRows.length) {
        sections.push(this.renderSection('Voyage Information', voyageRows.join('')))
      }
      
      // Performance (ходовые характеристики)
      const performanceRows = []
      if (data.avg_sog) {
        performanceRows.push(this.renderFieldRow('Average Speed', `${this.escapeHtml(String(data.avg_sog))} kn`))
      }
      if (data.max_sog) {
        performanceRows.push(this.renderFieldRow('Max Speed', `${this.escapeHtml(String(data.max_sog))} kn`))
      }
      if (data.distance_covered) {
        performanceRows.push(this.renderFieldRow('Distance Covered', `${this.escapeHtml(String(data.distance_covered))} nm`))
      }
      
      if (performanceRows.length) {
        sections.push(this.renderSection('Performance', performanceRows.join('')))
      }
      
      // Weather (метеоусловия)
      const weatherRows = []
      if (data.wind_knots) {
        const windDir = data.wind_direction ? ` ${this.escapeHtml(String(data.wind_direction))}` : ''
        weatherRows.push(this.renderFieldRow('Wind', `${this.escapeHtml(String(data.wind_knots))} kn${windDir}`))
      }
      if (data.temperature) {
        weatherRows.push(this.renderFieldRow('Temperature', `${this.escapeHtml(String(data.temperature))} °C`))
      }
      if (data.humidity) {
        weatherRows.push(this.renderFieldRow('Humidity', `${this.escapeHtml(String(data.humidity))} %`))
      }
      if (data.pressure) {
        weatherRows.push(this.renderFieldRow('Pressure', `${this.escapeHtml(String(data.pressure))} mb`))
      }
      if (data.visibility) {
        weatherRows.push(this.renderFieldRow('Visibility', `${this.escapeHtml(String(data.visibility))} m`))
      }
      
      if (weatherRows.length) {
        sections.push(this.renderSection('Weather', weatherRows.join('')))
      }
      
      return sections.join('')
    },

    /**
     * Отрендерить секцию
     */
    renderSection(title, bodyHtml) {
      if (!bodyHtml) {
        return ''
      }
      return `
        <div class="ship-popup-section">
          <div class="ship-popup-section-title">${title}</div>
          <div class="ship-popup-section-body">
            ${bodyHtml}
          </div>
        </div>
      `
    },

    /**
     * Отрендерить ряд поля
     */
    renderFieldRow(label, valueHtml) {
      if (!valueHtml) {
        return ''
      }
      return `
        <div class="ship-popup-field-row">
          <div class="ship-popup-field-sub-label">${label}</div>
          <div class="ship-popup-field-sub-value">${valueHtml}</div>
        </div>
      `
    },

    /**
     * Получить расширенные данные AIS для корабля
     */
    async fetchAISDetails(target) {
      try {
        // Используем MMSI как приоритет, если нет - IMO
        const identifier = target.mmsi || target.imo
        if (!identifier) {
          console.warn('No MMSI or IMO available for target:', target)
          return
        }

        // Определяем какой параметр использовать
        const paramName = target.mmsi ? 'mmsi' : 'imo'
        
        console.log(`[AIS Details] Fetching ${paramName}=${identifier} via elBackAxios`)

        // Use elBackAxios instead of direct fetch
        const response = await elBackAxios.get('api/ais/altposition', {
          params: { [paramName]: identifier }
        })

        const jsonResponse = response.data
        console.log(`[AIS Details] Response for ${paramName}=${identifier}:`, jsonResponse)

        if (jsonResponse.status === 'success' && jsonResponse.data) {
          this.expandedPopupData = jsonResponse.data
          this.expandedPopupTargetId = target.id
          this.showPopup(target)
        }

      } catch (error) {
        console.error('[AIS Details] Error fetching details:', error)
      }
    },

    /**
     * Форматировать дату/время
     */
    formatDatetime(value) {
      if (!value) {
        return null
      }
      try {
        const date = new Date(value)
        if (isNaN(date.getTime())) {
          return null
        }
        return date.toISOString().replace('T', ' ').substring(0, 16)
      } catch {
        return null
      }
    },
    escapeHtml(value) {
      return String(value ?? '').replace(/[&<>"']/g, char => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[char] || char))
    },
    formatNumber(value, suffix) {
      const num = Number(value)
      if (!Number.isFinite(num)) {
        return '—'
      }
      return `${num.toFixed(1)} ${suffix}`
    },
    updateOwnShip() {
      if (!this.map) {
        return
      }
      const pos = this.ownship
      if (!pos || !Number.isFinite(pos.lat) || !Number.isFinite(pos.lon)) {
        if (this.shipMarker) {
          this.map.removeLayer(this.shipMarker)
          this.shipMarker = null
        }
        return
      }
      const latlng = [pos.lat, pos.lon]
      const icon = this.makeOwnShipIcon(pos.cog)
      if (!this.shipMarker) {
        this.shipMarker = L.marker(latlng, { icon }).addTo(this.map)
      } else {
        this.shipMarker.setLatLng(latlng)
        this.shipMarker.setIcon(icon)
      }
    },
    makeOwnShipIcon(heading = 0) {
      return createShipIcon({
        width: 28,
        height: 56,
        scale: 0.7,
        headingDeg: heading || 0,
        fillColor: '#1d4ed8',
        strokeColor: '#bfdbfe',
        strokeWidth: 1.4,
        className: 'ais-ship-icon',
        alt: 'Ship',
      })
    },
    updateRoute() {
      if (!this.map || !this.routeLayer) {
        return
      }
      this.routeLayer.clearLayers()
      const coords = (this.route || [])
        .filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lon))
        .map(point => [point.lat, point.lon])
      if (coords.length >= 2) {
        L.polyline(coords, {
          color: '#ea580c',
          weight: 3,
          opacity: 0.85,
        }).addTo(this.routeLayer)
      }
    },
    focusOnTarget(target, options = {}) {
      if (!this.map || !this.isTargetValid(target)) {
        return
      }
      const { pan = false, openPopup = false } = options
      const latlng = [target.lat, target.lon]
      if (pan) {
        this.map.flyTo(latlng, Math.max(this.map.getZoom(), 12), { duration: 0.6 })
      }
      if (openPopup) {
        this.showPopup(target)
      }
    },
    fitToTargets(options = {}) {
      if (!this.map) {
        return
      }
      const includeRoute = Boolean(options.includeRoute)
      const coords = []
      // Собираем все координаты целей, маршрута и собственного корабля для подгонки карты
      const list = this.targets || []
      list.forEach(target => {
        if (this.isTargetValid(target)) {
          coords.push([target.lat, target.lon])
        }
      })
      if (this.ownship && Number.isFinite(this.ownship.lat) && Number.isFinite(this.ownship.lon)) {
        coords.push([this.ownship.lat, this.ownship.lon])
      }
      if (includeRoute && Array.isArray(this.route)) {
        this.route.forEach(point => {
          if (Number.isFinite(point.lat) && Number.isFinite(point.lon)) {
            coords.push([point.lat, point.lon])
          }
        })
      }
      if (!coords.length) {
        return
      }
      const bounds = L.latLngBounds(coords)
      this.map.flyToBounds(bounds, { padding: [40, 40], maxZoom: 14, duration: 0.7 })
    },
    getCurrentBounds() {
      if (!this.map) {
        return null
      }
      const bounds = this.map.getBounds()
      return {
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      }
    },
    getCurrentZoom() {
      return this.map ? this.map.getZoom() : null
    },
  },
}
</script>

<style scoped>
.ais-map-container {
  width: 100%;
  height: 100%;
  min-height: 420px;
}

/* ==================== AIS Popup Styles ==================== */

:deep(.ais-map-popup .leaflet-popup-content-wrapper) {
  border-radius: 12px;
  padding: 0;
  background: white;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border: none;
  min-width: 300px;
  max-width: 300px;
}

:deep(.ais-map-popup .leaflet-popup-content) {
  margin: 0;
  padding: 0;
}

:deep(.ship-popup-content) {
  width: 300px;
  max-height: 600px;
  overflow-y: auto;
  box-sizing: border-box;
}

:deep(.ship-popup-content.scrollable-content) {
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}

:deep(.ship-popup-content::-webkit-scrollbar) {
  width: 6px;
}

:deep(.ship-popup-content::-webkit-scrollbar-track) {
  background: #f1f1f1;
  border-radius: 3px;
}

:deep(.ship-popup-content::-webkit-scrollbar-thumb) {
  background: #c1c1c1;
  border-radius: 3px;
}

:deep(.ship-popup-content::-webkit-scrollbar-thumb:hover) {
  background: #a1a1a1;
}

:deep(.ship-popup-card) {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f5f7ff 0%, #e6e9ff 100%);
  border: 1px solid rgba(98, 111, 149, 0.22);
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
  color: #1f2940;
  box-sizing: border-box;
}

:deep(.ship-popup-header) {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
}

:deep(.ship-popup-header-info) {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

:deep(.ship-popup-label) {
  font-size: 0.68rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 600;
  color: #556089;
}

:deep(.ship-popup-title) {
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #131a2d;
}

:deep(.ship-popup-status) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  box-shadow: inset 0 0 0 1px rgba(98, 111, 149, 0.16);
  white-space: normal;
  background: #eef1fb;
  color: #4a5374;
  text-align: center;
  max-width: 100px;
  word-break: break-word;
  line-height: 1.2;
}

:deep(.ship-popup-status.ship-popup-status-success),
:deep(.ship-popup-status.ship-popup-status-warning),
:deep(.ship-popup-status.ship-popup-status-secondary),
:deep(.ship-popup-status.ship-popup-status-danger),
:deep(.ship-popup-status.ship-popup-status-info) {
  background: #eef1fb;
  color: #4a5374;
}



:deep(.ship-popup-info-grid) {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 14px;
  border: 1px solid rgba(226, 229, 240, 0.9);
  box-shadow: 0 10px 20px rgba(20, 30, 55, 0.12);
  box-sizing: border-box;
}

:deep(.ship-popup-info-row) {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
}

:deep(.ship-popup-info-label) {
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: #727c9b;
  flex-shrink: 0;
}

:deep(.ship-popup-info-value) {
  font-size: 0.86rem;
  font-weight: 500;
  color: #1f2940;
  text-align: right;
  word-break: break-word;
}

:deep(.ship-popup-details-container) {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

:deep(.ship-popup-toggle) {
  padding: 6px 16px;
  border-radius: 14px;
  border: 1px solid rgba(52, 70, 164, 0.28);
  background: rgba(229, 235, 255, 0.85);
  color: #2c3a82;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
  width: 100%;
  text-align: center;
}

:deep(.ship-popup-toggle:hover) {
  background: rgba(229, 235, 255, 1);
  transform: translateY(-1px);
}

:deep(.ship-popup-toggle:active) {
  transform: translateY(0);
}

:deep(.ship-popup-section) {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 14px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  border: 1px solid rgba(226, 229, 240, 0.85);
  box-shadow: 0 12px 24px rgba(24, 32, 60, 0.12);
  box-sizing: border-box;
}

:deep(.ship-popup-section-title) {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 600;
  color: #56618a;
}

:deep(.ship-popup-section-body) {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

:deep(.ship-popup-field-row) {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

:deep(.ship-popup-field-sub-label) {
  font-size: 0.64rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #7a84a6;
}

:deep(.ship-popup-field-sub-value) {
  font-size: 0.82rem;
  font-weight: 500;
  color: #1f2940;
  word-break: break-word;
}

:deep(.ais-ship-icon img) {
  display: block;
}

:deep(.ais-ship-icon img) {
  display: block;
}

:deep(.ais-target-icon img) {
  display: block;
}
</style>
